import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import { identifySentryUser } from '@/lib/sentry'
import { useQueryClient } from '@tanstack/react-query'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { getAal, type Aal } from '@/lib/mfa'

interface AuthValue {
  session: Session | null
  user: User | null
  companyId: string | null
  role: string | null
  firstName: string
  loading: boolean
  /** Current session assurance level (aal1 = password only, aal2 = second factor satisfied). */
  aal: Aal
  /** The account has a verified factor this session has not yet satisfied. false for non-MFA users. */
  mfaRequired: boolean
  refreshAal: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [aal, setAal] = useState<Aal>(null)
  const [mfaRequired, setMfaRequired] = useState(false)

  const refreshAal = useCallback(async () => {
    const { data } = await supabase.auth.getSession()
    if (!data.session) { setAal(null); setMfaRequired(false); return }
    const { current, next } = await getAal()
    setAal(current)
    setMfaRequired(current === 'aal1' && next === 'aal2')
  }, [])

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setLoading(false)
      void refreshAal()
    })
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      // Wipe every cached query whenever the identity changes.
      //
      // Without this the TanStack cache survives a sign-out, so the NEXT user is served the
      // previous tenant's rows from cache while their own query refetches in the background.
      // That is not a database leak (RLS scopes correctly), it is the browser showing one
      // company's data to someone from another. It also disguises itself as a sync failure:
      // A customer's client list read 8 for a few moments, which was the demo tenant's cached
      // count, before their real 1,606 arrived.
      if (event === 'SIGNED_OUT' || event === 'SIGNED_IN') queryClient.clear()
      setSession(s)
      void refreshAal()
    })
    return () => sub.subscription.unsubscribe()
  }, [refreshAal, queryClient])

  const user = session?.user ?? null
  const app = (user?.app_metadata ?? {}) as Record<string, unknown>
  const meta = (user?.user_metadata ?? {}) as Record<string, unknown>

  // An error report says WHERE and WHAT; this is the only WHO it gets. The id answers "is this
  // one business or all of them", which is the first useful question about any new issue. No
  // email, no name — see the scrubbing rules in src/lib/sentry.ts.
  useEffect(() => {
    identifySentryUser(user?.id ?? null, (app.company_id as string) ?? null)
  }, [user?.id, app.company_id])

  const value: AuthValue = {
    session,
    user,
    companyId: (app.company_id as string) ?? null,
    role: (app.role as string) ?? null,
    firstName: (meta.first_name as string) ?? (user?.email?.split('@')[0] ?? ''),
    loading,
    aal,
    mfaRequired,
    refreshAal,
    signOut: async () => {
      // Clear before the sign-out resolves: anything still mounted must not be able to
      // re-read another account's rows from cache on the way out.
      queryClient.clear()
      await supabase.auth.signOut()
    },
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
